/**
 * The author's key, in words, for whoever is marking.
 *
 * The scorer in `services/questionScoring.js` reads the same fields to decide
 * a machine-marked answer. This turns them into a sentence instead, so a
 * teacher marking a written answer next to an auto-marked one is looking at
 * the rule that was actually applied, not their memory of it.
 *
 * Kept in step with the scorer's fallbacks: an empty `textRule.value` falls
 * back to `correct.answer`, because older questions stored the key there.
 */

import {
  ANSWER_MODES,
  blankValuesFrom,
  countFillBlanks,
  expectedOptionIds,
  normalizeCorrectKey,
  normalizeQuestionOptions,
  normalizeScoring,
  optionId,
  padBlankValues,
  type ChoiceOption,
  type CorrectKey,
  type MatchingPair,
  type Scoring,
} from "./questionScoring";

export type KeyedQuestion = {
  type: string;
  prompt?: string | null;
  options?: unknown;
  correct?: CorrectKey | null;
  scoring?: (Partial<Scoring> & { presentation?: string }) | null;
};

export type AnswerKey = {
  /** What kind of rule this is: "Correct option", "Text match", "Manual". */
  label: string;
  /** The expected answers themselves, one per line. Empty when there is no key. */
  lines: string[];
  /** A caveat worth reading before you mark — case sensitivity, partial match. */
  note?: string;
};

function isPair(row: { id: number } | MatchingPair): row is MatchingPair {
  return "left" in row;
}

export function answerKeyFor(question: KeyedQuestion): AnswerKey {
  const type = String(question?.type || "");
  if (type === "directions") {
    return { label: "Directions", lines: [], note: "Not scored." };
  }

  const scoring = normalizeScoring(question?.scoring, type);
  const presentation = question?.scoring?.presentation || "";
  const options = normalizeQuestionOptions(question?.options, type, presentation);
  const mode = scoring.answerMode;

  if (mode === ANSWER_MODES.MANUAL) {
    return {
      label: "Manual",
      lines: [],
      note: "The author left this for you to judge. There is no key.",
    };
  }

  if (mode === ANSWER_MODES.FREE) {
    return {
      label: "Free",
      lines: [],
      note: "Any answer earns full marks. Blank earns none.",
    };
  }

  if (mode === ANSWER_MODES.CHOICES) {
    const wanted = new Set(
      expectedOptionIds(
        { type, options: question?.options, correct: question?.correct, presentation },
        scoring,
      ),
    );
    const lines = options
      .filter((row) => wanted.has(row.id) && !isPair(row))
      .map((row) => String((row as { label: string }).label || "").trim())
      .filter(Boolean);
    return {
      label: lines.length > 1 ? "Correct options" : "Correct option",
      lines,
      note: lines.length ? undefined : "No correct option was set on this question.",
    };
  }

  if (mode === ANSWER_MODES.PAIRS) {
    const lines = options
      .filter(isPair)
      .map((pair) => `${pair.left} → ${pair.right}`)
      .filter((line) => line.trim() !== "→");
    return {
      label: "All pairs",
      lines,
      note: lines.length ? "Every pair must match." : "No pairs were set.",
    };
  }

  if (mode === ANSWER_MODES.TEXT) {
    const key = normalizeCorrectKey(question?.correct, options, type);
    const rule = scoring.textRule;
    const casing = rule.caseSensitive ? "Case matters." : "Case is ignored.";

    if (type === "fill_blank") {
      const count = Math.max(1, countFillBlanks(String(question?.prompt || "")));
      const values = blankValuesFrom(rule, key);
      const lines = values
        .slice(0, count)
        .map((value, i) => `Blank ${i + 1}: ${value || "—"}`);
      return { label: "Fill the blanks", lines, note: casing };
    }

    const value = rule.value || String(key.answer ?? "");
    const match = rule.match === "equals" ? "Must match exactly." : "Must contain this.";
    return {
      label: "Text match",
      lines: value ? [value] : [],
      note: value ? `${match} ${casing}` : "No text was set to match against.",
    };
  }

  if (mode === ANSWER_MODES.NUMBER_EXACT) {
    const exact = String(scoring.numberRule.exact ?? "");
    return { label: "Is exactly", lines: exact === "" ? [] : [exact] };
  }

  if (mode === ANSWER_MODES.NUMBER_RANGE) {
    const { min, max } = scoring.numberRule;
    const has = String(min ?? "") !== "" && String(max ?? "") !== "";
    return { label: "Between", lines: has ? [`${min} to ${max}`] : [], note: has ? "Inclusive." : undefined };
  }

  if (mode === ANSWER_MODES.DATE_EXACT) {
    const exact = String(scoring.dateRule.exact ?? "");
    return { label: "Is exactly", lines: exact === "" ? [] : [exact] };
  }

  if (mode === ANSWER_MODES.DATE_RANGE) {
    const { min, max } = scoring.dateRule;
    const has = String(min ?? "") !== "" && String(max ?? "") !== "";
    return { label: "Date range", lines: has ? [`${min} to ${max}`] : [], note: has ? "Inclusive." : undefined };
  }

  return { label: "No key", lines: [] };
}

export type TheirFile = {
  name: string;
  url: string | null;
  mime: string | null;
};

/**
 * A stored response, as a bag of fields.
 *
 * Answers are ids and maps (`optionId`, `pairs: { 1: 5 }`), not the labels the
 * learner clicked, and choice, matching, and upload have no `answer` key at
 * all — reading that one field is how a scored True/False came out as "No
 * answer". Everything below resolves the ids against the question's options.
 */
function responseRecord(answer: unknown): Record<string, unknown> {
  if (answer == null || answer === "") return {};
  if (typeof answer === "string" || typeof answer === "number" || typeof answer === "boolean") {
    return { answer: String(answer) };
  }
  if (typeof answer === "object" && !Array.isArray(answer)) return answer as Record<string, unknown>;
  return {};
}

function choiceIdsFrom(src: Record<string, unknown>): number[] {
  const many = Array.isArray(src.optionIds)
    ? src.optionIds.map(optionId).filter((id): id is number => id != null)
    : [];
  if (many.length) return [...new Set(many)];
  const one = optionId(src.optionId ?? src.choiceId);
  return one != null ? [one] : [];
}

function filesFrom(src: Record<string, unknown>): TheirFile[] {
  const out: TheirFile[] = [];
  const seen = new Set<string>();
  const push = (name: string, url: string | null, mime: string | null) => {
    const key = url || name;
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({ name: name || "File", url, mime });
  };
  if (Array.isArray(src.files)) {
    for (const raw of src.files) {
      if (!raw || typeof raw !== "object") continue;
      const file = raw as { name?: unknown; url?: unknown; mime?: unknown };
      const url = typeof file.url === "string" && file.url.trim() ? file.url.trim() : null;
      const name = typeof file.name === "string" && file.name.trim() ? file.name.trim() : url ? url.split("/").pop() || "File" : "File";
      const mime = typeof file.mime === "string" ? file.mime : null;
      push(name, url, mime);
    }
  }
  const urls = Array.isArray(src.urls) ? src.urls : typeof src.url === "string" ? [src.url] : [];
  for (const raw of urls) {
    if (typeof raw !== "string" || !raw.trim()) continue;
    const url = raw.trim();
    push(url.split("/").pop() || "File", url, null);
  }
  return out;
}

/**
 * The student's answer, laid out the way the question was asked.
 *
 * The grader used to flatten every answer to a list of lines and stand it
 * beside a second list of the right ones. On a ten-pair matching question that
 * is two columns of "Apple → 3" and a teacher diffing them by eye to find the
 * one that is wrong. This keeps the shape — options stay options, pairs stay
 * pairs — and says per row whether it holds up against the key, so the screen
 * can colour the wrong one instead of asking someone to spot it.
 *
 * Row verdicts follow `services/questionScoring.js`, not a second opinion:
 * a pair counts when the learner joined a left to the right stored on the same
 * row, and a blank counts under the author's own match and casing rule. Where
 * the rule is not reproducible here (a number range, a text answer the server
 * judged), `verdict` carries what the server already decided.
 */
export type ReviewState = "right" | "wrong" | "unknown";

export type ReviewOptionRow = {
  id: number;
  label: string;
  chosen: boolean;
  expected: boolean;
};

export type ReviewPairRow = {
  id: number;
  left: string;
  theirs: string;
  expected: string;
  state: ReviewState;
};

export type ReviewFieldRow = {
  label: string | null;
  theirs: string;
  expected: string;
  state: ReviewState;
};

export type ResponseReview =
  | { kind: "none"; keyKnown: false }
  | { kind: "options"; keyKnown: boolean; rows: ReviewOptionRow[] }
  | { kind: "pairs"; keyKnown: boolean; rows: ReviewPairRow[] }
  | { kind: "fields"; keyKnown: boolean; rows: ReviewFieldRow[] }
  | { kind: "files"; keyKnown: false; files: TheirFile[] };

/** The scorer's text comparison, so a blank is not marked two different ways. */
function textMatches(
  rule: { match: "includes" | "equals"; caseSensitive: boolean },
  expected: string,
  value: string,
) {
  const target = String(expected ?? "");
  if (!target.trim()) return false;
  const a = rule.caseSensitive ? String(value ?? "") : String(value ?? "").toLowerCase();
  const b = rule.caseSensitive ? target : target.toLowerCase();
  return rule.match === "equals" ? a === b : a.includes(b);
}

export function reviewFor(
  question: KeyedQuestion,
  answer: unknown,
  verdict?: boolean | null,
): ResponseReview {
  const type = String(question?.type || "");
  if (type === "directions") return { kind: "none", keyKnown: false };

  const scoring = normalizeScoring(question?.scoring, type);
  const presentation = question?.scoring?.presentation || "";
  const options = normalizeQuestionOptions(question?.options, type, presentation);
  const mode = scoring.answerMode;
  // Manual and Free have no right answer to check a row against.
  const judged = mode !== ANSWER_MODES.MANUAL && mode !== ANSWER_MODES.FREE;
  const src = responseRecord(answer);

  if (type === "upload") {
    return { kind: "files", keyKnown: false, files: filesFrom(src) };
  }

  if (type === "matching") {
    const pairs = options.filter(isPair);
    const byId = new Map(pairs.map((pair) => [pair.id, pair]));
    const given =
      src.pairs && typeof src.pairs === "object" && !Array.isArray(src.pairs)
        ? (src.pairs as Record<string, unknown>)
        : {};
    const complete = pairs.filter(
      (pair) => String(pair.left || "").trim() && String(pair.right || "").trim(),
    );
    const keyKnown = judged && complete.length >= 2;
    const rows: ReviewPairRow[] = pairs.map((pair) => {
      const rightId = optionId(given[pair.id] ?? given[String(pair.id)]);
      const theirs = rightId == null ? "" : byId.get(rightId)?.right || `#${rightId}`;
      return {
        id: pair.id,
        left: String(pair.left || "").trim() || `#${pair.id}`,
        theirs: String(theirs || "").trim(),
        expected: String(pair.right || "").trim(),
        state: !keyKnown ? "unknown" : rightId === pair.id ? "right" : "wrong",
      };
    });
    return { kind: "pairs", keyKnown, rows };
  }

  const choiceType =
    type === "single_choice" ||
    type === "multiple_choice" ||
    type === "true_false" ||
    type === "yes_no";
  if (choiceType) {
    const chosen = new Set(choiceIdsFrom(src));
    const expected = new Set(
      judged
        ? expectedOptionIds(
            { type, options: question?.options, correct: question?.correct, presentation },
            scoring,
          )
        : [],
    );
    const rows: ReviewOptionRow[] = options
      .filter((row): row is ChoiceOption => !isPair(row))
      .map((row) => ({
        id: row.id,
        label: String(row.label || "").trim() || `#${row.id}`,
        chosen: chosen.has(row.id),
        expected: expected.has(row.id),
      }));
    return { kind: "options", keyKnown: expected.size > 0, rows };
  }

  if (type === "fill_blank") {
    const count = Math.max(1, countFillBlanks(String(question?.prompt || "")));
    const expected = padBlankValues(blankValuesFrom(scoring.textRule, question?.correct), count);
    const blanks = Array.isArray(src.blanks) ? src.blanks.map((value) => String(value ?? "")) : [];
    const keyKnown = judged && expected.every((value) => String(value).trim() !== "");
    const rows: ReviewFieldRow[] = expected.map((want, i) => {
      const raw = blanks[i] ?? (count === 1 ? src.answer : "");
      const theirs = String(raw ?? "").trim();
      return {
        label: count === 1 ? null : `Blank ${i + 1}`,
        theirs,
        expected: String(want).trim(),
        state: !keyKnown ? "unknown" : textMatches(scoring.textRule, want, theirs) ? "right" : "wrong",
      };
    });
    return { kind: "fields", keyKnown, rows };
  }

  const key = answerKeyFor(question);
  const theirs = String(src.answer ?? src.text ?? "").trim();
  return {
    kind: "fields",
    keyKnown: judged && key.lines.length > 0,
    rows: [
      {
        label: null,
        theirs,
        expected: key.lines.join("\n"),
        // A Manual essay marked 2 of 4 is not "wrong", and saying so in red
        // over the child's own paragraph is the screen overreaching. Those
        // keep no row verdict; the points on the card carry the judgement.
        state: !judged || verdict == null ? "unknown" : verdict ? "right" : "wrong",
      },
    ],
  };
}
